import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { wormColors } from "core/src/core/constants.js";
import { seatTag } from "core/src/core/replay-poster.js";
import { arenaLabel } from "../setup/maps";
import { displayRating } from "../../shared/rating/rating";
import type { MatchHistoryRow } from "../../shared/rating/store";
import { rankedLabel, rankedModeOfMatchId } from "../../shared/rating/ranked";
import { fetchPoster, replayMayExist, replayExpired, replayPath, REPLAY_DAYS, type ReplayPoster } from "../../shared/replays";
import styles from "./MatchesTab.module.scss";

// Profile > Matches. Matches are grouped into the sessions they were played in (an
// evening's play reads as one thing, under its day), and each match is a card you
// can recognise: the arena as its last round
// left it, who took each round (yours raised, and counted when you won any), and one
// obvious way in to the replay.
//
// What a card draws comes from the match's POSTER (core replay-poster.js), a few KB
// the host saves with the replay. A match without one (before replays existed, or
// past the days they are kept) still lists, as a plain card.

const SHOWN = 30;
// A session's day turns over at 05:00, so a night that runs past midnight stays one session.
const DAY_TURNS_AT_HOUR = 5;
const BOX_TITLES: Record<string, string> = { small: "Duel", medium: "Brawl", large: "Royale Chaos" };

const dr = (mu: number) => displayRating({ mu, sigma: 0 });
function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
export function boxTitle(matchId: string | null): string | null {
  const ranked = rankedLabel(rankedModeOfMatchId(matchId));
  if (ranked) return ranked;
  const m = matchId ? /^public:([a-z]+)#/.exec(matchId) : null;
  return m ? BOX_TITLES[m[1]] || null : null;
}
function sessionDay(iso: string): Date {
  const d = new Date(iso);
  d.setHours(d.getHours() - DAY_TURNS_AT_HOUR);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function dayLabel(day: Date): string {
  const today = sessionDay(new Date().toISOString());
  const diff = Math.round((today.getTime() - day.getTime()) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return day.toLocaleDateString(undefined, diff < 7 ? { weekday: "long" } : { weekday: "long", day: "numeric", month: "short" });
}
const clock = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

interface Session { day: Date; matches: MatchHistoryRow[] } // matches newest first

function toSessions(history: MatchHistoryRow[]): Session[] {
  const out: Session[] = [];
  for (const h of [...history].reverse().slice(0, SHOWN)) {
    const day = sessionDay(h.createdAt);
    const last = out[out.length - 1];
    if (last && last.day.getTime() === day.getTime()) last.matches.push(h);
    else out.push({ day, matches: [h] });
  }
  return out;
}

// The arena as the match's last round left it. A non-square map (the 2:1
// rectangle or stadium) is fitted into the square thumbnail and centred,
// letterboxed; a stadium is clipped and outlined as a pill. An older poster
// without `shape` goes by `round` (a circle) and is otherwise a rectangle.
function PosterArena({ poster }: { poster: ReplayPoster }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const S = canvas.width, ctx = canvas.getContext("2d")!;
    const span = Math.max(poster.map.width, poster.map.height) || 1, k = S / span;
    const W = (poster.map.width || span) * k, H = (poster.map.height || span) * k;
    const ox = (S - W) / 2, oy = (S - H) / 2;
    const shape = poster.map.shape || (poster.map.round ? "circle" : "rectangle");
    // A pill inset by `inset` from the map's box (its long axis along x, like core's stadium).
    const pill = (inset: number) => {
      const r = Math.max(0, Math.min(W, H) / 2 - inset);
      ctx.beginPath();
      ctx.roundRect(ox + inset, oy + inset, Math.max(0, W - 2 * inset), Math.max(0, H - 2 * inset), r);
    };
    ctx.clearRect(0, 0, S, S);
    ctx.save();
    if (shape === "circle") { ctx.beginPath(); ctx.arc(S / 2, S / 2, S / 2 - 1, 0, Math.PI * 2); ctx.clip(); }
    else if (shape === "stadium") { pill(0); ctx.clip(); }
    else { ctx.beginPath(); ctx.rect(ox, oy, W, H); ctx.clip(); }
    ctx.translate(ox, oy);
    ctx.fillStyle = "#0b101d";
    ctx.fillRect(0, 0, W, H);
    ctx.lineWidth = Math.max(1.5, S / 70);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const t of poster.trails) {
      ctx.strokeStyle = (wormColors as any)[t.c as any] || "#8995b3";
      for (const l of t.l) {
        ctx.beginPath();
        ctx.moveTo(l[0] * k, l[1] * k);
        for (let i = 2; i < l.length; i += 2) ctx.lineTo(l[i] * k, l[i + 1] * k);
        ctx.stroke();
      }
    }
    ctx.restore();
    ctx.strokeStyle = "#2b3b56";
    ctx.lineWidth = Math.max(2, S / 56);
    if (shape === "circle") { ctx.beginPath(); ctx.arc(S / 2, S / 2, S / 2 - ctx.lineWidth / 2, 0, Math.PI * 2); ctx.stroke(); }
    else if (shape === "stadium") { pill(ctx.lineWidth / 2); ctx.stroke(); }
    else ctx.strokeRect(ox + ctx.lineWidth / 2, oy + ctx.lineWidth / 2, W - ctx.lineWidth, H - ctx.lineWidth);
  }, [poster]);
  return <canvas ref={ref} className={styles.arena + ((poster.map.shape || (poster.map.round ? "circle" : "")) === "circle" ? " " + styles.arenaRound : "")} width={176} height={176} />;
}

function MatchCard({ h, poster, myTag }: { h: MatchHistoryRow; poster: ReplayPoster | null | undefined; myTag: string | null }) {
  const delta = dr(h.muAfter) - dr(h.muBefore);
  const tone = h.placement === 1 ? styles.win : delta >= 0 ? styles.up : styles.down;
  const watchable = !!h.matchId && replayMayExist(h.createdAt) && poster !== null;
  const mySeat = poster && myTag ? poster.seats.find((s) => s.tag === myTag) : null;
  const colorOf = (playerId: string | null) => {
    const seat = poster && poster.seats.find((s) => s.id === playerId);
    return (seat && (wormColors as any)[seat.colorId]) || "#55607a";
  };
  const taken = poster && mySeat ? poster.rounds.filter((r) => r.winner === mySeat.id).length : null;
  const where = [boxTitle(h.matchId), h.map ? arenaLabel(h.map) : null, h.playstyle].filter(Boolean).join(" · ");

  const body = (
    <>
      <div className={styles.thumb}>
        {poster ? <PosterArena poster={poster} /> : <div className={styles.noArena}>{watchable ? "" : "No replay"}</div>}
        {watchable && <span className={styles.play} aria-hidden="true"><svg viewBox="0 0 24 24" width="16" height="16"><path d="M8 5.5v13l11-6.5z" fill="currentColor" /></svg></span>}
      </div>
      <div className={styles.main}>
        <div className={styles.top}>
          <span className={`${styles.place} ${tone}`}>{h.placement === 1 ? "Won" : ordinal(h.placement)} of {h.fieldSize}</span>
          <span className={styles.where}>{where}</span>
        </div>
        {poster && poster.rounds.length > 0 && (
          <div className={styles.pips} aria-label="Who took each round">
            {poster.rounds.map((r, i) => (
              <span
                key={i}
                className={styles.pip + (mySeat && r.winner === mySeat.id ? " " + styles.pipMine : "")}
                style={{ background: colorOf(r.winner) }}
                title={`Round ${i + 1}: ${(poster.seats.find((s) => s.id === r.winner) || { name: "?" }).name}${mySeat ? ", you " + (r.order.indexOf(mySeat.id) === 0 ? "won" : "came " + ordinal(r.order.indexOf(mySeat.id) + 1)) : ""}`}
              />
            ))}
          </div>
        )}
        <div className={styles.meta}>
          {clock(h.createdAt)}
          {taken ? ` · You won ${taken} ${taken === 1 ? "round" : "rounds"}` : ""}
          {replayExpired(h.createdAt) ? ` · replays are kept ${REPLAY_DAYS} days` : ""}
        </div>
      </div>
      <div className={styles.side}>
        <span className={`${styles.delta} ${tone}`}>{delta >= 0 ? "+" + delta : "−" + Math.abs(delta)}</span>
        {watchable && <span className={styles.watch}>Watch replay<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg></span>}
      </div>
    </>
  );
  // The whole card is the way in: one big target, not a small tag to aim for.
  return watchable
    ? <Link className={`${styles.card} ${styles.cardLink}`} to={replayPath(h.matchId!, mySeat ? mySeat.id : null)} title="Watch this match">{body}</Link>
    : <div className={`${styles.card} ${styles.cardPlain}`}>{body}</div>;
}

// debugTag: fixtures only (/debug/matches), the seat tag to treat as "you".
export default function MatchesTab({ history, loading, accountId, debugTag }: { history: MatchHistoryRow[] | null; loading: boolean; accountId: string | null; debugTag?: string }) {
  const sessions = useMemo(() => toSessions(history || []), [history]);
  // matchId -> poster; null = the match has none (it still lists); undefined = still loading.
  const [posters, setPosters] = useState<Record<string, ReplayPoster | null>>({});
  const [myTag, setMyTag] = useState<string | null>(debugTag ?? null);

  useEffect(() => {
    let live = true;
    if (accountId) seatTag(accountId).then((t: string) => live && setMyTag(t)).catch(() => {});
    return () => { live = false; };
  }, [accountId]);

  useEffect(() => {
    let live = true;
    for (const s of sessions) for (const h of s.matches) {
      if (!h.matchId || !replayMayExist(h.createdAt)) continue;
      const id = h.matchId;
      fetchPoster(id).then((p) => { if (live) setPosters((prev) => (id in prev ? prev : { ...prev, [id]: p })); });
    }
    return () => { live = false; };
  }, [sessions]);

  if (loading) {
    return (
      <div className={styles.sessions}>
        <div className={styles.session}>
          <div className={styles.head}><span className={styles.skel} style={{ width: 70 }} /><span className={styles.skel} style={{ width: 110 }} /></div>
          {[0, 1, 2, 3].map((i) => <div key={i} className={`${styles.card} ${styles.cardPlain}`}><div className={styles.thumb}><div className={styles.noArena} /></div><div className={styles.main}><span className={styles.skel} style={{ width: i % 2 ? 120 : 90 }} /><span className={styles.skel} style={{ width: "70%" }} /></div></div>)}
        </div>
      </div>
    );
  }
  if (!sessions.length) return <div className={styles.empty}>No online matches yet.</div>;

  return (
    <div className={styles.sessions}>
      {sessions.map((s) => (
        <section key={s.day.getTime()} className={styles.session}>
          <div className={styles.head}>
            <h3 className={styles.day}>{dayLabel(s.day)}</h3>
            <span className={styles.count}>{s.matches.length} {s.matches.length === 1 ? "match" : "matches"}</span>
          </div>
          {s.matches.map((h, i) => <MatchCard key={(h.matchId || "") + i} h={h} poster={h.matchId && replayMayExist(h.createdAt) ? posters[h.matchId] : null} myTag={myTag} />)}
        </section>
      ))}
    </div>
  );
}
